import type { MetaAdDraftSummary } from '../../types/metaAdDraft';

// Stage 7A E1 — pure client-side list view: derive the display status, filter by
// status tab, search by title, and sort. The list endpoint returns ALL summaries
// (incl. archived/soft-deleted); everything below shapes the view (no server
// round-trips). Pure + unit-tested.

// What the row badge shows. "archived" is DERIVED from deletedAt — it is not a
// MetaAdDraftStatus value (soft-delete is the archive, see CLAUDE.md).
export type DraftDisplayStatus =
  | 'draft'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'cancelled'
  | 'archived';

export type DraftStatusFilter = 'all' | 'draft' | 'published' | 'archived';

export const DRAFT_STATUS_FILTER_OPTIONS: Array<{ value: DraftStatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

export type DraftSortKey = 'updated' | 'created' | 'title';
export type DraftSort = { key: DraftSortKey; dir: 'asc' | 'desc' };
export const DEFAULT_DRAFT_SORT: DraftSort = { key: 'updated', dir: 'desc' };

export function displayStatus(d: Pick<MetaAdDraftSummary, 'status' | 'deletedAt'>): DraftDisplayStatus {
  if (d.deletedAt) return 'archived';
  return d.status;
}

// A failed-publish indicator shows when a prior publish attempt left an error AND
// the draft is back to an editable, non-archived state (so it's actionable).
export function hasFailedPublishIndicator(d: MetaAdDraftSummary): boolean {
  return d.lastPublishError !== null && d.deletedAt === null && d.status !== 'published';
}

export function filterDraftsByStatus(
  drafts: MetaAdDraftSummary[],
  filter: DraftStatusFilter,
): MetaAdDraftSummary[] {
  switch (filter) {
    case 'all':
      return drafts;
    case 'archived':
      return drafts.filter((d) => d.deletedAt !== null);
    case 'published':
      return drafts.filter((d) => d.deletedAt === null && d.status === 'published');
    case 'draft':
      // Everything live that isn't published (draft / publishing / failed / cancelled).
      return drafts.filter((d) => d.deletedAt === null && d.status !== 'published');
    default:
      return drafts;
  }
}

export function searchDraftsByTitle(drafts: MetaAdDraftSummary[], query: string): MetaAdDraftSummary[] {
  const q = query.trim().toLowerCase();
  if (q === '') return drafts;
  return drafts.filter((d) => (d.title || 'untitled').toLowerCase().includes(q));
}

const titleOf = (d: MetaAdDraftSummary) => (d.title.trim() || 'Untitled').toLowerCase();
const ms = (iso: string | null) => (iso ? new Date(iso).getTime() : 0);

export function sortDrafts(drafts: MetaAdDraftSummary[], sort: DraftSort): MetaAdDraftSummary[] {
  const factor = sort.dir === 'asc' ? 1 : -1;
  const cmp = (a: MetaAdDraftSummary, b: MetaAdDraftSummary): number => {
    if (sort.key === 'title') return titleOf(a).localeCompare(titleOf(b)) * factor;
    const av = sort.key === 'created' ? ms(a.createdAt) : ms(a.updatedAt);
    const bv = sort.key === 'created' ? ms(b.createdAt) : ms(b.updatedAt);
    if (av !== bv) return (av - bv) * factor;
    // Stable tiebreak by id so equal timestamps don't reorder between renders.
    return a.id.localeCompare(b.id);
  };
  return [...drafts].sort(cmp);
}

// Toggle helper for clickable sort headers: same key flips dir, new key starts desc
// (except title, which starts asc — alphabetical reads better A→Z first).
export function nextDraftSort(cur: DraftSort, key: DraftSortKey): DraftSort {
  if (cur.key === key) return { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: key === 'title' ? 'asc' : 'desc' };
}

// The full client pipeline: filter → search → sort.
export function viewDrafts(
  drafts: MetaAdDraftSummary[],
  opts: { status: DraftStatusFilter; search: string; sort: DraftSort },
): MetaAdDraftSummary[] {
  return sortDrafts(
    searchDraftsByTitle(filterDraftsByStatus(drafts, opts.status), opts.search),
    opts.sort,
  );
}
